"use client";

import { useRouter } from "@/lib/useRouter";
import React, { useLayoutEffect, useEffect, useRef } from "react";

interface MetaHeadProps {
  /**
   * Meta & Open Graph title
   */
  title?: string;
  /***
   * Robots should either be 'follow, index', 'nofollow' or 'none' i.e. 'nofollow, noindex'
   * There are other options but make sure you have a very good use case before adding it
   */
  robots?: "follow index" | "nofollow" | "none";
  /**
   * Meta & Open Graph description
   */
  description?: string;
  /**
   * Open graph types
   */
  ogType?: "article" | "book" | "profile" | "website";
  /**
   * Open Graph image
   */
  ogImage?: string;
  /**
   * Open Graph image alt text for visually impaired
   */
  ogImageAlt?: string;
  /**
   * Author of the post or article. Please see open graph docs for formatting.
   */
  articleAuthor?: string;
  articleModifyTime?: string;
  articlePublishTime?: string;
  /**
   * Add only if custom URL or breaks the pattern of https://gettactic.com/path i.e. tabs
   */
  pageURL?: string;
  /**
   * URL that we want search engines to index as the primary page
   */
  canonicalURL?: string;
  /**
   * Any additional custom meta information
   */
  children?: React.ReactNode;
}

/**
 * Helper function to get or create a meta tag
 */
function getOrCreateMetaTag(
  property: string,
  attribute: "name" | "property" = "property"
): HTMLMetaElement {
  const selector =
    attribute === "property"
      ? `meta[property="${property}"]`
      : `meta[name="${property}"]`;

  let element = document.querySelector(selector) as HTMLMetaElement;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, property);
    document.head.appendChild(element);
  }
  return element;
}

/**
 * Helper function to get or create a link tag
 */
function getOrCreateLinkTag(rel: string): HTMLLinkElement {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  return element;
}

/**
 * Component for adding meta information to pages, posts,
 * etc. Things like open graph metadata and featured images.
 * Updated for Next.js App Router compatibility.
 */
export const MetaHead: React.FC<MetaHeadProps> = ({
  title = "Where office and remote work meet",
  description = "Helping proactive companies return to the office and make long-term decisions backed by data.",
  ogType = "website",
  ogImage = "https://res.cloudinary.com/tactic/image/upload/v1607208968/og_image_gavs72.png",
  ogImageAlt = "Tactic helps proactive companies return to the office and make long-term decisions backed by data.",
  articleAuthor,
  articlePublishTime,
  articleModifyTime,
  pageURL,
  canonicalURL,
  robots,
  children
}) => {
  const router = useRouter();
  // Only append "| Tactic" if the title doesn't already end with "Tactic"
  const fullTitle = title.endsWith("Tactic") ? title : `${title} | Tactic`;
  const titleRef = useRef<string>(fullTitle);
  const isActiveRef = useRef(true);

  // Update ref when title changes
  titleRef.current = fullTitle;
  isActiveRef.current = true;

  // Set title immediately and periodically check for changes from Next.js metadata
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const currentTitle = titleRef.current;

    // Set title immediately
    const setTitle = () => {
      if (!isActiveRef.current) return;
      if (document.title !== currentTitle) {
        document.title = currentTitle;
      }
      let titleElement = document.querySelector("title");
      if (!titleElement) {
        titleElement = document.createElement("title");
        document.head.insertBefore(titleElement, document.head.firstChild);
      }
      if (titleElement.textContent !== currentTitle) {
        titleElement.textContent = currentTitle;
      }
    };

    setTitle();

    // Set again after short delays to catch Next.js metadata application
    const timeout1 = setTimeout(setTitle, 0);
    const timeout2 = setTimeout(setTitle, 100);
    const timeout3 = setTimeout(setTitle, 500);
    const timeout4 = setTimeout(setTitle, 1000);
    const timeout5 = setTimeout(setTitle, 2000);

    return () => {
      isActiveRef.current = false;
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
      clearTimeout(timeout5);
    };
  }, [title]);

  // Set all meta tags in useEffect
  useEffect(() => {
    if (typeof window === "undefined") return;

    const ogUrl = pageURL ? pageURL : `https://gettactic.com${router.asPath}`;
    const canonical = canonicalURL
      ? canonicalURL
      : `https://gettactic.com${router.asPath}`;

    // Set meta description
    const descriptionMeta = getOrCreateMetaTag("description", "name");
    descriptionMeta.content = description;

    // Set meta author
    const authorMeta = getOrCreateMetaTag("author", "name");
    authorMeta.content = "Tactic, Inc.";

    // Set robots meta
    if (robots) {
      const robotsMeta = getOrCreateMetaTag("robots", "name");
      robotsMeta.content = robots;
    }

    // Set Open Graph type
    const ogTypeMeta = getOrCreateMetaTag("og:type");
    ogTypeMeta.content = ogType;

    // Set Open Graph site name
    const ogSiteMeta = getOrCreateMetaTag("og:site_name");
    ogSiteMeta.content = "Tactic";

    // Set Open Graph locale
    const ogLocaleMeta = getOrCreateMetaTag("og:locale");
    ogLocaleMeta.content = "en_US";

    // Set Open Graph title
    const ogTitleMeta = getOrCreateMetaTag("og:title");
    ogTitleMeta.content = fullTitle;

    // Set Open Graph image
    const ogImageMeta = getOrCreateMetaTag("og:image");
    ogImageMeta.content = ogImage;

    // Set Open Graph image alt
    const ogImageAltMeta = getOrCreateMetaTag("og:image:alt");
    ogImageAltMeta.content = ogImageAlt;

    // Set Open Graph description
    const ogDescMeta = getOrCreateMetaTag("og:description");
    ogDescMeta.content = description;

    // Set Open Graph URL
    const ogUrlMeta = getOrCreateMetaTag("og:url");
    ogUrlMeta.content = ogUrl;

    // Handle article-specific meta tags
    if (ogType === "article") {
      if (articlePublishTime) {
        const ogArticlePublishMeta = getOrCreateMetaTag(
          "article:published_time"
        );
        ogArticlePublishMeta.content = articlePublishTime;
      }
      if (articleModifyTime) {
        const ogArticleModifyMeta = getOrCreateMetaTag("article:modified_time");
        ogArticleModifyMeta.content = articleModifyTime;
      }
      if (articleAuthor) {
        const ogArticleAuthorMeta = getOrCreateMetaTag("article:author");
        ogArticleAuthorMeta.content = articleAuthor;
      }
    } else {
      // Remove article-specific tags if not an article
      const articlePublish = document.querySelector(
        'meta[property="article:published_time"]'
      );
      const articleModify = document.querySelector(
        'meta[property="article:modified_time"]'
      );
      const articleAuthorTag = document.querySelector(
        'meta[property="article:author"]'
      );
      articlePublish?.remove();
      articleModify?.remove();
      articleAuthorTag?.remove();
    }

    // Set canonical URL
    const canonicalLink = getOrCreateLinkTag("canonical");
    canonicalLink.href = canonical;

    // Set Twitter meta tags
    const twitterSiteMeta = getOrCreateMetaTag("twitter:site", "name");
    twitterSiteMeta.content = "@tacticsoftware";

    const twitterCardMeta = getOrCreateMetaTag("twitter:card", "name");
    twitterCardMeta.content = "summary_large_image";
  }, [
    title,
    description,
    ogType,
    ogImage,
    ogImageAlt,
    articleAuthor,
    articlePublishTime,
    articleModifyTime,
    pageURL,
    canonicalURL,
    robots,
    router.asPath,
    fullTitle
  ]);

  // Render children if provided (for custom meta tags)
  return <>{children}</>;
};
